import * as THREE from 'three';
import { hexToWorld, type BattleState, type DungeonLayout, type MapDef } from '@somnhold/sim';
import { emis } from '../materials.ts';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import type { CryptBuild } from './crypt.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { clamp01 } from './halls.ts';

/**
 * The Drinker (El pozo 2, docs/SAGAS.md): the crypt re-dressed as the Order's old waterworks under Musgoviejo. The
 * first room is the foot of the well's shaft (a rope and a cold shaft of light from far above); then the dry cistern
 * (a stone basin cracked dry, drain grates, mineral rings where the water stood); then the pipes (big bronze and rust
 * pipes round the walls on their brackets, valve wheels, a burst one); and last the vein, a warm cave where the rock
 * sweats, wet black boulders round its walls, puddles, and in the middle a crack in the floor lit warm from inside,
 * where the leech drinks. The rune circle and the Warden's violet light go: it is not his hall.
 *
 * The flood: once the Drinker bursts, the water comes back up the rooms the way the heroes came down (the chain of room
 * centres, vein first, as `drinkerWet` in sim/battle/drinker.ts). One merged sheet over every floor cell carries how far
 * along that chain each vertex is; a shader shows it up to the water's front, a band of white foam at the front, and
 * spray thrown up across the way at the front each tick; heroes in it throw splashes.
 *
 * Draw calls: the dressing (one merged mesh), the wet rock (one, glossy), the water (one), a glow layer. No lights of
 * its own: the crypt's room lights are re-coloured.
 */

const STONE = ['#585a61', '#4d4f56', '#62646b'];
const DRY = '#6e6a62';
const SALT = '#8f8a7e';
const GRATE = '#141417';
const BRONZE = '#6b4f2e';
const RUST = '#5a3424';
const VERDIGRIS = '#3f6a5c';
const ROCK = ['#17161b', '#1e1c22', '#121115'];
const ROPE = '#6b5a3e';
const WARM = new THREE.Color('#ff9a4a');
const WARM_CORE = new THREE.Color('#ffd2a0');
const COOL = new THREE.Color('#cfe8ff');
const SWEAT = new THREE.Color('#9fd0ff');
const SWELL = new THREE.Color('#bfe6ff');
const INK_SPIT = '#0c0914';

type Pt = { x: number; y: number };

/** The flood's water: shown up to the front, foam at its edge, a slow ripple. */
const waterMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { front: { value: -1 }, time: { value: 0 }, foam: { value: 900 } },
    vertexShader: /* glsl */ `
      attribute float along;
      varying float vAlong;
      varying vec2 vXZ;
      uniform float time;
      void main() {
        vAlong = along;
        vXZ = position.xz;
        vec3 p = position;
        p.y += 0.025 * sin(p.x * 2.3 + time * 2.1) * cos(p.z * 1.9 - time * 1.7);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying float vAlong;
      varying vec2 vXZ;
      uniform float front;
      uniform float time;
      uniform float foam;
      void main() {
        if (vAlong > front) discard;
        float edge = smoothstep(front - foam, front, vAlong);
        float rip = 0.5 + 0.5 * sin(vXZ.x * 3.1 + vXZ.y * 2.7 + time * 3.0);
        vec3 deep = mix(vec3(0.10, 0.24, 0.32), vec3(0.16, 0.36, 0.46), rip * 0.5);
        vec3 col = mix(deep, vec3(0.92, 0.97, 1.0), edge * edge);
        float a = mix(0.78, 0.95, edge);
        gl_FragColor = vec4(col, a);
      }`,
  });

export class PipesLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(260);
  private water: THREE.Mesh;
  private waterM: THREE.ShaderMaterial;
  /** The rooms' chain the water comes up, vein first (sim units), its segments' lengths, and in world. */
  private chain: Pt[] = [];
  private lens: number[] = [];
  /** The vein's crack (world), the shaft's foot, the cistern's and the vein's sweat points. */
  private vein = new THREE.Vector3();
  private shaft = new THREE.Vector3();
  private sweat: THREE.Vector3[] = [];
  /** Where the Drinker was last seen (sim), for its burst. */
  private last: Pt = { x: 0, y: 0 };
  private now = 0;
  private v = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    crypt: CryptBuild,
    rooms: THREE.PointLight[],
  ) {
    this.group.name = 'pipes';
    const L = map.dungeon as DungeonLayout;
    const S = WORLD_PER_HEX;
    // Not the Warden's hall: no rune circle, and the vein's light is warm, not violet.
    crypt.runes.visible = false;
    // The vein's sconces burn warm too.
    const warmFlame = emis('#ffd2a0', '#ff8a3a', 1.6, { transparent: true, opacity: 0.95, depthWrite: false });
    for (const f of crypt.flames) {
      if (f.room !== L.rooms.length - 1) continue;
      (f.mesh as THREE.Mesh).material = warmFlame;
      (f.glow.material as THREE.SpriteMaterial).color.set('#ff9a3a');
    }
    const lastLight = rooms[rooms.length - 1];
    if (lastLight) {
      lastLight.color.set(0xff8a3a);
      lastLight.intensity = 20;
    }
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
    const ringG = new THREE.CylinderGeometry(0.5, 0.5, 1, 18, 1, true);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const disc = new THREE.CircleGeometry(0.5, 16).rotateX(-Math.PI / 2);
    const torus = new THREE.TorusGeometry(0.5, 0.12, 6, 16);
    const kit = new Kit();
    const wet = new Kit();
    const last = L.rooms.length - 1;
    L.rooms.forEach((room, i) => {
      const w = hexToWorld(room.q, room.r);
      const c = ctx.at(w.x, w.y, 0);
      const rw = room.radius * S;
      if (i === 0) this.foot(kit, box, cyl, rock, c);
      else if (i === last) this.cave(kit, wet, box, rock, disc, c, rw, i);
      else if (i === 1) this.cistern(kit, box, cyl, ringG, rock, disc, c, rw, i);
      else this.pipes(kit, box, cyl, torus, rock, c, rw, i);
    });
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.1, flatShading: true });
    const wetMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.18, metalness: 0.35, flatShading: true });
    this.mats.push(mat, wetMat);
    for (const [k, m, n] of [
      [kit, mat, 'pipes-dress'],
      [wet, wetMat, 'pipes-wet'],
    ] as const) {
      const mesh = k.build(m, n);
      if (!mesh) continue;
      mesh.receiveShadow = true;
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }

    // The way the water comes back: the rooms' centres, vein first.
    this.chain = L.rooms
      .slice()
      .reverse()
      .map((r) => hexToWorld(r.q, r.r));
    for (let i = 1; i < this.chain.length; i++) {
      const a = this.chain[i - 1] as Pt;
      const b = this.chain[i] as Pt;
      this.lens.push(Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)));
    }
    // The water: a hexagon over every floor cell, each corner carrying how far along the chain it is.
    const pos: number[] = [];
    const along: number[] = [];
    const rad = S / Math.sqrt(3);
    const k = S / 1000;
    for (const h of L.floor) {
      const w = hexToWorld(h.q, h.r);
      const cx = w.x * k;
      const cz = w.y * k;
      const y = ctx.ground(cx, cz) + 0.16;
      const corner = (i: number): [number, number] => {
        const a = (Math.PI / 180) * (60 * i - 30);
        return [cx + Math.cos(a) * rad * 1.02, cz + Math.sin(a) * rad * 1.02];
      };
      const ca = this.along(cx / k, cz / k);
      for (let i = 0; i < 6; i++) {
        const [ax, az] = corner(i);
        const [bx, bz] = corner((i + 1) % 6);
        pos.push(cx, y, cz, bx, y, bz, ax, y, az);
        along.push(ca, this.along(bx / k, bz / k), this.along(ax / k, az / k));
      }
    }
    const wg = new THREE.BufferGeometry();
    wg.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    wg.setAttribute('along', new THREE.Float32BufferAttribute(along, 1));
    wg.computeBoundingSphere();
    this.geos.push(wg);
    this.waterM = waterMat();
    this.mats.push(this.waterM);
    this.water = new THREE.Mesh(wg, this.waterM);
    this.water.name = 'pipes-flood';
    this.water.renderOrder = 3;
    this.water.frustumCulled = false;
    this.water.visible = false;
    this.group.add(this.water);

    this.group.add(this.glow.mesh);
    for (const g of [box, cyl, ringG, rock, disc, torus]) g.dispose();
    this.group.updateMatrixWorld(true);
  }

  /** How far up the way (sim units, from the vein) a sim point is: as sim/battle/drinker.ts `along`. */
  private along(x: number, y: number): number {
    let best = Infinity;
    let at = 0;
    let run = 0;
    for (let i = 1; i < this.chain.length; i++) {
      const a = this.chain[i - 1] as Pt;
      const b = this.chain[i] as Pt;
      const len = this.lens[i - 1] as number;
      let t = ((x - a.x) * (b.x - a.x) + (y - a.y) * (b.y - a.y)) / len;
      t = Math.max(0, Math.min(len, t));
      const px = a.x + ((b.x - a.x) * t) / len;
      const py = a.y + ((b.y - a.y) * t) / len;
      const d = (x - px) ** 2 + (y - py) ** 2;
      if (d < best) {
        best = d;
        at = run + t;
      }
      run += len;
    }
    return at;
  }

  /** The point `d` up the way from the vein (sim), and the way's direction there. */
  private pointAt(d: number): { p: Pt; dx: number; dy: number } {
    let run = 0;
    for (let i = 1; i < this.chain.length; i++) {
      const a = this.chain[i - 1] as Pt;
      const b = this.chain[i] as Pt;
      const len = this.lens[i - 1] as number;
      if (d <= run + len || i === this.chain.length - 1) {
        const t = Math.max(0, Math.min(1, (d - run) / len));
        return { p: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, dx: (b.x - a.x) / len, dy: (b.y - a.y) / len };
      }
      run += len;
    }
    const z = this.chain[0] ?? { x: 0, y: 0 };
    return { p: z, dx: 0, dy: 1 };
  }

  /** The entrance: the foot of the well's shaft, a rope hanging down into a cold shaft of light, a fallen bucket. */
  private foot(kit: Kit, box: THREE.BoxGeometry, cyl: THREE.CylinderGeometry, rock: THREE.DodecahedronGeometry, c: THREE.Vector3): void {
    const g = c.y;
    kit.add(cyl, STONE[1] as string, place(c.x, g + 0.03, c.z, 0, new THREE.Vector3(1.1, 0.06, 1.1)));
    kit.add(box, ROPE, place(c.x + 0.12, g + 1.6, c.z, 0, new THREE.Vector3(0.035, 3.2, 0.035)));
    for (let i = 0; i < 4; i++) kit.add(box, ROPE, place(c.x + 0.12 + Math.cos(i * 1.6) * 0.12, g + 0.03, c.z + Math.sin(i * 1.6) * 0.12, i * 1.6, new THREE.Vector3(0.24, 0.035, 0.035)));
    kit.add(cyl, '#5a4130', place(c.x - 0.4, g + 0.1, c.z + 0.25, 0, new THREE.Vector3(0.2, 0.18, 0.2), Math.PI / 2, 0.4));
    for (let i = 0; i < 5; i++) kit.add(rock, STONE[i % 3] as string, place(c.x + Math.cos(i * 1.3) * 0.75, g + 0.05, c.z + Math.sin(i * 1.3) * 0.75, i, 0.15 + (i % 2) * 0.06));
    this.shaft.set(c.x + 0.12, g, c.z);
  }

  /** The dry cistern: a round stone basin cracked dry, mineral rings where the water stood, grates, a broken pail. */
  private cistern(kit: Kit, box: THREE.BoxGeometry, cyl: THREE.CylinderGeometry, ringG: THREE.CylinderGeometry, rock: THREE.DodecahedronGeometry, disc: THREE.BufferGeometry, c: THREE.Vector3, rw: number, i: number): void {
    const g = c.y;
    const r = rw * 0.42;
    kit.add(disc, DRY, place(c.x, g + 0.02, c.z, 0, new THREE.Vector3(r * 2, 1, r * 2)));
    for (const k of [0.92, 0.7, 0.46]) kit.add(new THREE.RingGeometry(0.48, 0.5, 28).rotateX(-Math.PI / 2), SALT, place(c.x, g + 0.026, c.z, 0, new THREE.Vector3(r * 2 * k, 1, r * 2 * k)));
    kit.add(ringG, STONE[0] as string, place(c.x, g + 0.16, c.z, 0, new THREE.Vector3(r * 2.1, 0.32, r * 2.1)));
    kit.add(ringG, STONE[2] as string, place(c.x, g + 0.16, c.z, 0, new THREE.Vector3(r * 1.9, 0.32, r * 1.9)));
    kit.add(new THREE.TorusGeometry(0.5, 0.05, 4, 24).rotateX(Math.PI / 2), STONE[1] as string, place(c.x, g + 0.32, c.z, 0, new THREE.Vector3(r * 2.04, 1, r * 2.04)));
    // Cracks across the dry bottom.
    for (let k = 0; k < 7; k++) {
      const a = k * 0.9 + i;
      kit.add(box, '#2a2826', place(c.x + Math.cos(a) * r * 0.4, g + 0.03, c.z + Math.sin(a) * r * 0.4, -a, new THREE.Vector3(r * 0.7, 0.01, 0.025)));
    }
    // The drain grates round the walls, a dry spout over each.
    for (let k = 0; k < 3; k++) {
      const a = (k / 3) * Math.PI * 2 + 0.5 + i;
      const x = c.x + Math.cos(a) * rw * 0.78;
      const z = c.z + Math.sin(a) * rw * 0.78;
      kit.add(box, '#2c2d31', place(x, g + 0.02, z, -a, new THREE.Vector3(0.5, 0.03, 0.5)));
      for (let b = 0; b < 4; b++) kit.add(box, GRATE, place(x, g + 0.04, z, -a, new THREE.Vector3(0.44, 0.02, 0.05), 0, 0).multiply(place(0, 0, (b - 1.5) * 2.2)));
      kit.add(cyl, BRONZE, place(x + Math.cos(a) * 0.4, g + 0.75, z + Math.sin(a) * 0.4, 0, new THREE.Vector3(0.14, 0.4, 0.14), Math.PI / 2 - 0.3, 0).multiply(place(0, 0, 0, -a + Math.PI / 2)));
    }
    kit.add(cyl, '#5a4130', place(c.x + r * 0.5, g + 0.09, c.z - r * 0.3, 0, new THREE.Vector3(0.18, 0.16, 0.18), Math.PI / 2, 1.1));
    for (let k = 0; k < 6; k++) kit.add(rock, STONE[k % 3] as string, place(c.x + Math.cos(k * 2.1 + i) * rw * 0.6, g + 0.05, c.z + Math.sin(k * 2.1 + i) * rw * 0.6, k, 0.14 + (k % 3) * 0.05));
  }

  /** The pipes: bronze and rust mains round the walls on brackets, valve wheels, one burst open, dry. */
  private pipes(kit: Kit, box: THREE.BoxGeometry, cyl: THREE.CylinderGeometry, torus: THREE.TorusGeometry, rock: THREE.DodecahedronGeometry, c: THREE.Vector3, rw: number, i: number): void {
    const g = c.y;
    const R = rw * 0.86;
    const n = 14;
    for (const [h, rr, col] of [
      [0.32, 0.17, BRONZE],
      [0.82, 0.12, RUST],
    ] as const) {
      for (let k = 0; k < n; k++) {
        // Leave the way in and out clear: the room's two door sides get no pipe low down.
        const a0 = (k / n) * Math.PI * 2;
        const a1 = ((k + 1) / n) * Math.PI * 2;
        const x0 = c.x + Math.cos(a0) * R;
        const z0 = c.z + Math.sin(a0) * R;
        const x1 = c.x + Math.cos(a1) * R;
        const z1 = c.z + Math.sin(a1) * R;
        const len = Math.hypot(x1 - x0, z1 - z0);
        const yaw = Math.atan2(x1 - x0, z1 - z0);
        const tint = (k + i) % 5 === 0 ? VERDIGRIS : col;
        kit.add(cyl, tint, place((x0 + x1) / 2, g + h, (z0 + z1) / 2, yaw, new THREE.Vector3(rr * 2, len * 1.02, rr * 2), Math.PI / 2));
        if (k % 2 === 0) kit.add(torus, '#3a2c1e', place(x0, g + h, z0, yaw, rr * 2.4).multiply(place(0, 0, 0, 0, 1, 0, 0)));
        if (h < 0.5 && k % 3 === 0) kit.add(box, '#2a2622', place(x0, g + h * 0.5, z0, yaw, new THREE.Vector3(0.08, h, 0.08)));
      }
    }
    // Valve wheels on the low main.
    for (let k = 0; k < 3; k++) {
      const a = (k / 3) * Math.PI * 2 + 0.9 + i;
      const x = c.x + Math.cos(a) * (R - 0.32);
      const z = c.z + Math.sin(a) * (R - 0.32);
      // Its wheel stands up facing the room.
      const yaw = Math.atan2(c.x - x, c.z - z);
      kit.add(cyl, '#2f3137', place(x, g + 0.48, z, yaw, new THREE.Vector3(0.05, 0.3, 0.05)));
      kit.add(new THREE.TorusGeometry(0.16, 0.025, 5, 14), '#7a2e22', place(x, g + 0.68, z, yaw));
      for (let s = 0; s < 3; s++) kit.add(box, '#7a2e22', place(x, g + 0.68, z, yaw, new THREE.Vector3(0.3, 0.02, 0.02), 0, (s * Math.PI) / 3));
    }
    // A burst main: its end split open, a heap of fallen rust and stone under it.
    const a = 2.2 + i;
    const bx = c.x + Math.cos(a) * R;
    const bz = c.z + Math.sin(a) * R;
    for (let k = 0; k < 5; k++) kit.add(box, RUST, place(bx, g + 0.32, bz, a + k, new THREE.Vector3(0.06, 0.2, 0.14), 0.5 * k, 0.4));
    for (let k = 0; k < 5; k++) kit.add(rock, k % 2 ? RUST : (STONE[1] as string), place(bx - Math.cos(a) * 0.35 + (k - 2) * 0.1, g + 0.05, bz - Math.sin(a) * 0.35, k, 0.12 + (k % 2) * 0.05));
  }

  /** The vein: a warm cave whose rock sweats, wet boulders round it, puddles, the crack lit from inside in the middle. */
  private cave(kit: Kit, wet: Kit, box: THREE.BoxGeometry, rock: THREE.DodecahedronGeometry, disc: THREE.BufferGeometry, c: THREE.Vector3, rw: number, i: number): void {
    const g = c.y;
    this.vein.set(c.x, g, c.z);
    // Wet black boulders heaped round the hall's rim.
    for (let k = 0; k < 22; k++) {
      const a = (k / 22) * Math.PI * 2 + i;
      const d = rw * (0.86 + ((k * 7) % 5) * 0.025);
      const s = 0.32 + ((k * 13) % 7) * 0.06;
      wet.add(rock, ROCK[k % 3] as string, place(c.x + Math.cos(a) * d, g + s * 0.35, c.z + Math.sin(a) * d, k * 1.7, new THREE.Vector3(s, s * (0.8 + (k % 3) * 0.25), s), k * 0.3, k * 0.2));
      if (k % 4 === 0) this.sweat.push(new THREE.Vector3(c.x + Math.cos(a) * (d - s * 0.3), g + s * 0.6, c.z + Math.sin(a) * (d - s * 0.3)));
    }
    // Puddles on the floor, glossy.
    for (let k = 0; k < 7; k++) {
      const a = k * 2.3 + i;
      const d = rw * (0.35 + (k % 3) * 0.14);
      wet.add(disc, '#2a3c48', place(c.x + Math.cos(a) * d, g + 0.022, c.z + Math.sin(a) * d, a, new THREE.Vector3(0.45 + (k % 3) * 0.18, 1, 0.3 + (k % 2) * 0.14)));
    }
    // The vein's crack: a star of dark splits, their lips raised.
    for (let k = 0; k < 6; k++) {
      const a = (k / 6) * Math.PI * 2 + 0.3;
      const len = 0.8 + (k % 3) * 0.3;
      kit.add(box, '#0a0806', place(c.x + Math.cos(a) * len * 0.5, g + 0.025, c.z + Math.sin(a) * len * 0.5, -a, new THREE.Vector3(len, 0.012, 0.09 - (k % 2) * 0.03)));
      kit.add(box, '#3a2a20', place(c.x + Math.cos(a) * len * 0.5, g + 0.04, c.z + Math.sin(a) * len * 0.5, -a, new THREE.Vector3(len * 0.9, 0.03, 0.03)).multiply(place(0, 0, 1.8)));
    }
    kit.add(disc, '#ff8a3a', place(c.x, g + 0.03, c.z, 0, new THREE.Vector3(0.35, 1, 0.35)));
  }

  /** Every fresh tick: the vein's warm breath and drips, the Drinker drinking and swelling, the burst, the flood's spray and splashes. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    const dr = s.enemies.find((e) => e.kind === 'drinker' && e.hp > 0);
    if (dr) this.last = { x: dr.x, y: dr.y };
    // Drips off the sweating rock.
    if (s.tick % (low ? 8 : 4) === 0 && this.sweat.length) {
      const p = this.sweat[(s.tick >> 2) % this.sweat.length] as THREE.Vector3;
      this.ctx.vfx.burst(p, { n: 1, color: '#a8d4ff', speed: 0.002, up: -0.01, size: 0.035, life: 16, gravity: 0.006, spread: 0.04 });
    }
    // Drinking: water drawn up out of the crack into its mouth.
    if (dr && dr.phase === 1 && !s.drink?.burst && s.tick % (low ? 3 : 1) === 0) {
      this.v.copy(this.vein).setY(this.vein.y + 0.05);
      this.ctx.vfx.burst(this.v, { n: 1, color: s.tick % 2 ? '#bfe6ff' : '#7fb8ff', speed: 0.01, up: 0.05, size: 0.05, life: 12, gravity: -0.002, spread: 0.25 });
    }
    // Swollen: water sloshing off it as it drags itself along.
    if (dr && dr.phase === 2 && s.tick % (low ? 6 : 3) === 0) {
      const p = this.ctx.at(dr.x, dr.y, 0.3);
      this.ctx.vfx.burst(p, { n: 1, color: '#bfe6ff', speed: 0.02, up: 0.02, size: 0.05, life: 12, gravity: 0.01, spread: 0.5 });
    }
    for (const e of s._events ?? []) {
      if (e.t !== 'drinker') continue;
      if (e.kind === 'swell') {
        const p = this.ctx.at(e.x, e.y, 0.6);
        this.ctx.flare(p, '#cfe8ff', 2.4, 12);
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.55), 0.3, 1.6, '#cfe8ff', 12, 0.8);
      } else if (e.kind === 'empty') {
        const p = this.ctx.at(e.x, e.y, 0.4);
        this.ctx.vfx.burst(p, { n: low ? 6 : 14, color: '#bfe6ff', speed: 0.06, up: 0.04, size: 0.06, life: 14, gravity: 0.01, spread: 0.6 });
      } else if (e.kind === 'burst') {
        // It pops: ink everywhere, a shock ring, a hard shake.
        const p = this.ctx.at(this.last.x, this.last.y, 0.5);
        this.ctx.shake(0.22);
        this.ctx.flare(p, '#5a3a8a', 3.2, 16);
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.45), 0.4, 3.2, '#3a2a5a', 16, 0.9);
        this.ctx.vfx.burst(p, { n: low ? 14 : 40, color: INK_SPIT, speed: 0.12, up: 0.1, size: 0.1, life: 30, gravity: 0.01, spread: 0.8 });
        this.ctx.vfx.burst(p, { n: low ? 4 : 10, color: '#2a2438', speed: 0.03, up: 0.03, size: 0.7, life: 40, smoke: true, spread: 0.8 });
      } else if (e.kind === 'wave') {
        // The roar in the pipes, then the wall of white water out of the vein.
        this.ctx.shake(0.3);
        this.ctx.flare(this.vein.clone().setY(this.vein.y + 0.8), '#ffffff', 3.6, 14);
        this.ctx.vfx.burst(this.vein.clone().setY(this.vein.y + 0.3), { n: low ? 14 : 36, color: '#eaf6ff', speed: 0.12, up: 0.16, size: 0.09, life: 24, gravity: 0.01, spread: 1 });
      } else if (e.kind === 'out') {
        const p = this.ctx.at(e.x, e.y, 0.6);
        this.ctx.flare(p, '#dfe9ff', 2.6, 16);
      }
    }
    // The flood: spray thrown up across the way at its front, splashes round whoever wades in it.
    const d = s.drink;
    if (!d?.burst || d.front <= 0) return;
    const f = this.pointAt(d.front);
    const nx = -f.dy;
    const ny = f.dx;
    const n = low ? 3 : 7;
    for (let k = 0; k < n; k++) {
      const off = ((k / (n - 1)) * 2 - 1) * 1900;
      const p = this.ctx.at(f.p.x + nx * off, f.p.y + ny * off, 0.2);
      this.ctx.vfx.burst(p, { n: 1, color: k % 2 ? '#ffffff' : '#d8ecff', speed: 0.03, up: 0.07, size: 0.09, life: 12, gravity: 0.012, spread: 0.3 });
    }
    if (s.tick % 4 === 0) this.ctx.vfx.burst(this.ctx.at(f.p.x, f.p.y, 0.3), { n: 1, color: '#e6f2ff', speed: 0.01, up: 0.02, size: 0.8, life: 18, smoke: true, spread: 0.8 });
    for (const h of s.heroes) {
      if (h.downTicks > 0 || (s.tick + h.id) % (low ? 6 : 3) !== 0 || this.along(h.x, h.y) > d.front) continue;
      const p = this.ctx.pos(h.id) ?? this.ctx.at(h.x, h.y, 0);
      this.v.copy(p).setY(this.ctx.ground(p.x, p.z) + 0.2);
      this.ctx.vfx.burst(this.v, { n: low ? 2 : 4, color: '#dff0ff', speed: 0.03, up: 0.05, size: 0.06, life: 10, gravity: 0.012, spread: 0.35 });
      this.ctx.vfx.ring(this.v.clone().setY(this.v.y - 0.02), 0.15, 0.6, '#cfe8ff', 8, 0.6);
    }
  }

  /** An ink spit from the Drinker to a hero: a black glob arcing over and splashing. */
  spit(a: THREE.Vector3, b: THREE.Vector3): void {
    const low = this.ctx.low;
    const m = a.clone().setY(a.y + 0.3);
    this.ctx.vfx.beam(m, b, INK_SPIT, 6, 0.09, 0.05);
    this.ctx.vfx.beam(m, b, '#3a2a5a', 4, 0.03, 0);
    this.ctx.vfx.burst(m, { n: low ? 3 : 6, color: INK_SPIT, speed: 0.04, up: 0.03, size: 0.07, life: 10, gravity: 0.01 });
    this.ctx.vfx.burst(b, { n: low ? 5 : 12, color: INK_SPIT, speed: 0.07, up: 0.05, size: 0.08, life: 16, gravity: 0.012, spread: 0.4 });
    this.ctx.vfx.ring(b.clone().setY(b.y - 0.5), 0.1, 0.8, '#3a2a5a', 8, 0.7);
  }

  /** Every redraw: the water up to its front, the vein's glow, the shaft's light, the Drinker swollen glowing through. */
  draw(s: BattleState): void {
    const d = s.drink;
    this.water.visible = !!d?.burst && d.front > 0;
    this.waterM.uniforms.front!.value = d?.front ?? -1;
    this.waterM.uniforms.time!.value = this.now;
    this.glow.begin();
    const beat = 0.5 + 0.5 * Math.sin(this.now * 2.2);
    // The vein, warm, beating; gone cold once the water is back.
    if (!d?.burst) {
      this.glow.add(this.vein.x, this.vein.y + 0.06, this.vein.z, WARM, 2.2, 0.35 + 0.2 * beat);
      this.glow.add(this.vein.x, this.vein.y + 0.08, this.vein.z, WARM_CORE, 0.7, 0.5 + 0.3 * beat);
    }
    // The cold light down the shaft at the entrance: the way back up.
    const shaft = d?.burst ? 0.75 + 0.25 * Math.sin(this.now * 5) : 0.45;
    for (let k = 0; k < 5; k++) this.glow.add(this.shaft.x, this.shaft.y + 0.4 + k * 0.7, this.shaft.z, COOL, 1.4 - k * 0.12, 0.12 * shaft);
    this.glow.add(this.shaft.x, this.shaft.y + 0.05, this.shaft.z, COOL, 2.0, 0.22 * shaft);
    // The sweating rock glints.
    this.sweat.forEach((p, i) => {
      const t = 0.5 + 0.5 * Math.sin(this.now * 1.3 + i * 1.7);
      this.glow.add(p.x, p.y, p.z, SWEAT, 0.25, 0.25 * t);
    });
    // The Drinker swollen: pale light through its skin (hit it now); drinking, a gulping pulse.
    const dr = s.enemies.find((e) => e.kind === 'drinker' && e.hp > 0);
    if (dr && (dr.phase === 2 || dr.phase === 1)) {
      const p = this.ctx.pos(dr.id) ?? this.ctx.at(dr.x, dr.y, 0);
      const full = dr.phase === 2;
      const k = full ? 0.75 + 0.25 * Math.sin(this.now * 6) : clamp01(0.4 + 0.3 * Math.sin(this.now * 9));
      this.glow.add(p.x, p.y + 0.55, p.z, SWELL, full ? 1.8 : 1.0, (full ? 0.22 : 0.14) * k);
      if (full) this.glow.add(p.x, this.ctx.ground(p.x, p.z) + 0.05, p.z, SWELL, 2.4, 0.2 * k);
    }
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
